/**
 * GroupSeatManagement Service
 * 그룹 좌석 관리 비즈니스 로직
 */
import { GroupType } from '../models/GroupSeatManagement';

export class GroupSeatManagementService {
  /**
   * 그룹 타입 라벨 조회
   */
  getGroupTypeLabel(type: GroupType): string {
    const labels: Record<GroupType, string> = {
      study_room: '스터디룸',
      meeting_room: '회의실',
      conference_room: '컨퍼런스룸',
      private_room: '개인룸',
    };
    return labels[type];
  }

  /**
   * 그룹 타입 색상 조회
   */
  getGroupTypeColor(type: GroupType): string {
    const colors: Record<GroupType, string> = {
      study_room: 'bg-blue-100 text-blue-800',
      meeting_room: 'bg-green-100 text-green-800',
      conference_room: 'bg-purple-100 text-purple-800',
      private_room: 'bg-yellow-100 text-yellow-800',
    };
    return colors[type];
  }

  /**
   * 시간당 요금 포맷팅
   */
  formatHourlyRate(rate: number): string {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(rate);
  }

  /**
   * 좌석 번호 목록 포맷팅
   */
  formatSeatNumbers(seats: number[]): string {
    if (seats.length === 0) return '좌석 없음';
    if (seats.length <= 3) return seats.join(', ');
    return `${seats.slice(0, 3).join(', ')} 외 ${seats.length - 3}개`;
  }

  /**
   * 그룹 폼 검증
   */
  validateGroupForm(
    name: string,
    capacity: number,
    hourlyRate: number,
    seatNumbers: number[]
  ): { isValid: boolean; error?: string } {
    if (!name.trim()) {
      return { isValid: false, error: '그룹 이름을 입력해주세요.' };
    }

    if (name.length > 50) {
      return { isValid: false, error: '그룹 이름은 50자 이내로 입력해주세요.' };
    }

    if (capacity < 1) {
      return { isValid: false, error: '수용 인원은 1명 이상이어야 합니다.' };
    }

    if (capacity > 20) {
      return { isValid: false, error: '수용 인원은 20명 이하여야 합니다.' };
    }

    if (hourlyRate < 0) {
      return { isValid: false, error: '시간당 요금은 0원 이상이어야 합니다.' };
    }

    if (seatNumbers.length === 0) {
      return { isValid: false, error: '최소 1개 이상의 좌석을 선택해주세요.' };
    }

    if (seatNumbers.length > capacity) {
      return {
        isValid: false,
        error: `선택한 좌석(${seatNumbers.length}개)이 수용 인원(${capacity}명)보다 많습니다.`,
      };
    }

    return { isValid: true };
  }

  /**
   * 좌석 존 색상 조회 (기본값 포함)
   */
  getZoneColorClass(zoneColor?: string): string {
    if (!zoneColor) return 'bg-gray-100 text-gray-800';

    const colorMap: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      purple: 'bg-purple-100 text-purple-800',
      pink: 'bg-pink-100 text-pink-800',
      indigo: 'bg-indigo-100 text-indigo-800',
      gray: 'bg-gray-100 text-gray-800',
    };

    return colorMap[zoneColor] || 'bg-gray-100 text-gray-800';
  }

  /**
   * 날짜 포맷팅
   */
  formatDate(dateString?: string): string {
    if (!dateString) return '-';

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

export const groupSeatManagementService = new GroupSeatManagementService();
